import { useId } from 'react';

// The one notice (ENTRAID-347 redesign, brand/onguard/README.md "Notices"): a white card with a 4 px left rule in the
// status color, a title in words, one or two sentences and at most one action. Never a tinted fill: the title carries
// the meaning, so color is never the only signal. Replaces the red, amber and indigo tinted boxes.

/** The left rule per tone (status tokens in src/styles.css). */
export const NOTICE_RULE = Object.freeze({
  info: 'var(--blue)',
  good: 'var(--good)',
  warn: 'var(--warn)',
  bad: 'var(--bad)',
});

/**
 * @param {{
 *   tone?: 'info'|'good'|'warn'|'bad', title: import('react').ReactNode, children?: import('react').ReactNode,
 *   action?: import('react').ReactNode, titleAs?: 'h2'|'h3'|'p', role?: string, className?: string,
 *   testId?: string, decoration?: import('react').ReactNode, dataAttrs?: Record<string, string>,
 * }} props
 * `role` defaults to "alert" for a bad notice (an error the reader must see) and "note" otherwise.
 */
export default function Notice({
  tone = 'info', title, children = null, action = null, titleAs: Title = 'h2', role, className = '', testId,
  decoration = null, dataAttrs = {},
}) {
  const titleId = useId();
  const rule = NOTICE_RULE[tone] ?? NOTICE_RULE.info;
  return (
    <section
      role={role ?? (tone === 'bad' ? 'alert' : 'note')}
      aria-labelledby={titleId}
      className={`relative flex flex-col gap-3 overflow-hidden rounded-xl border border-ipc-border bg-white px-[18px] py-3.5 sm:flex-row sm:items-center ${className}`.trim()}
      style={{ borderLeft: `4px solid ${rule}` }}
      data-notice={tone}
      data-testid={testId}
      {...dataAttrs}
    >
      {decoration}
      <div className="min-w-0 flex-1">
        <Title id={titleId} className="text-sm font-semibold text-ipc-ink">{title}</Title>
        {children ? <div className="mt-0.5 text-[13.5px] text-ipc-muted">{children}</div> : null}
      </div>
      {action ? <div className="shrink-0 sm:ml-auto" data-notice-action="">{action}</div> : null}
    </section>
  );
}
